import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useChatWebSocket } from './useChatWebSocket';

const ChatContext = createContext(null);

const SESSION_STORAGE_KEY = 'bluecmr_chat_messages';
const SESSION_ID_KEY = 'bluecmr_chat_session_id';
const CHAT_OPEN_KEY = 'bluecmr_chat_open';

/**
 * Load persisted messages from sessionStorage for cross-page continuity.
 */
function loadPersistedMessages() {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return parsed.map(m => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return [];
  }
}

function loadPersistedSessionId() {
  try {
    return sessionStorage.getItem(SESSION_ID_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * ChatProvider — persists chat state across navigation.
 * Messages have shape: { id, role: 'user'|'agent', content, timestamp, status?, steps? }
 * status: 'pending' | 'running' | 'done' | 'error'
 * steps: [{ label, status: 'pending'|'running'|'done'|'error' }]
 */
export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState(loadPersistedMessages);
  const [isOpen, setIsOpen] = useState(() => {
    try { return sessionStorage.getItem(CHAT_OPEN_KEY) === 'true'; } catch { return false; }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState(loadPersistedSessionId);
  const [conversationId, setConversationId] = useState(null);

  const toggleChat = useCallback(() => setIsOpen(prev => !prev), []);
  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const addMessage = useCallback((msg) => {
    setMessages(prev => [...prev, { ...msg, id: Date.now() + Math.random(), timestamp: new Date() }]);
  }, []);

  const updateLastAgentMessage = useCallback((updates) => {
    setMessages(prev => {
      const idx = [...prev].reverse().findIndex(m => m.role === 'agent');
      if (idx === -1) return prev;
      const realIdx = prev.length - 1 - idx;
      const updated = [...prev];
      const current = updated[realIdx];
      const resolved = {};
      for (const [key, value] of Object.entries(updates)) {
        resolved[key] = typeof value === 'function' ? value(current[key]) : value;
      }
      updated[realIdx] = { ...current, ...resolved };
      return updated;
    });
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  // Persist messages to sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(messages));
    } catch { /* quota exceeded — ignore */ }
  }, [messages]);

  // Persist sessionId
  useEffect(() => {
    try {
      if (sessionId) {
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      }
    } catch { /* ignore */ }
  }, [sessionId]);

  // Persist isOpen state
  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_OPEN_KEY, isOpen ? 'true' : 'false');
    } catch { /* ignore */ }
  }, [isOpen]);

  // WebSocket hook
  const handleWsMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'response':
        updateLastAgentMessage({
          content: msg.data.content,
          status: 'done',
          steps: (prev) => (Array.isArray(prev) ? prev : []).map(s => ({ ...s, status: 'done' }))
        });
        if (msg.data.conversationId) setConversationId(msg.data.conversationId);
        if (msg.data.sessionId) setSessionId(msg.data.sessionId);
        setIsProcessing(false);
        break;

      case 'textDelta':
        // Streaming: append text chunk to the last agent message
        updateLastAgentMessage({
          content: (prev) => (prev || '') + (msg.data.text || ''),
          status: 'streaming'
        });
        break;

      case 'step':
        updateLastAgentMessage({
          steps: (prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            const idx = existing.findIndex(s => s.label === msg.data.label);
            if (idx >= 0) {
              const updated = [...existing];
              updated[idx] = { ...updated[idx], status: msg.data.status };
              return updated;
            }
            return [...existing, { label: msg.data.label, status: msg.data.status }];
          }
        });
        break;

      case 'error':
        updateLastAgentMessage({
          content: msg.data.message || 'Error del sistema',
          status: 'error',
          steps: (prev) => (Array.isArray(prev) ? prev : []).map(s =>
            s.status === 'running' ? { ...s, status: 'error' } : s
          )
        });
        setIsProcessing(false);
        break;

      case 'history': {
        const historyMsgs = (msg.data.messages || []).map(m => ({
          id: m.messageId || Date.now() + Math.random(),
          role: m.role === 'assistant' ? 'agent' : m.role,
          content: m.content,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
          status: 'done'
        }));
        if (historyMsgs.length > 0) {
          setMessages(historyMsgs);
        }
        if (msg.data.conversationId) setConversationId(msg.data.conversationId);
        if (msg.data.sessionId) setSessionId(msg.data.sessionId);
        break;
      }

      case 'pong':
        break;

      default:
        break;
    }
  }, [updateLastAgentMessage]);

  const { connect, disconnect, send, connectionStatus } = useChatWebSocket(handleWsMessage);

  // Auto-connect WebSocket at provider level (once, on mount).
  // No disconnect on cleanup — the WebSocket hook handles its own lifecycle.
  // This prevents reconnection cycles when navigating between widget and /asesor-ia.
  useEffect(() => {
    connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send message via WebSocket
  const sendMessage = useCallback((message) => {
    if (connectionStatus !== 'connected') {
      console.warn('WebSocket not connected');
      return;
    }

    addMessage({ role: 'user', content: message });
    addMessage({ role: 'agent', content: '', status: 'pending', steps: [] });
    setIsProcessing(true);

    send({
      action: 'sendMessage',
      data: { message, conversationId }
    });
  }, [addMessage, conversationId, connectionStatus, send]);

  // Clear conversation on server
  const clearConversation = useCallback(() => {
    if (connectionStatus === 'connected') {
      send({
        action: 'clearConversation',
        data: { conversationId }
      });
    }
    clearMessages();
  }, [connectionStatus, conversationId, send, clearMessages]);

  return (
    <ChatContext.Provider value={{
      messages, isOpen, isProcessing, connectionStatus,
      sessionId, conversationId,
      toggleChat, openChat, closeChat,
      addMessage, updateLastAgentMessage, clearMessages,
      setIsProcessing,
      connect, disconnect, send, sendMessage, clearConversation,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};
